import { create, tsx } from '@dojo/framework/core/vdom';
import * as css from './Summary.m.css';
import { RenderResult } from '@dojo/framework/core/interfaces';
import { Text } from './Text';

export interface SummaryProperties {
	/** stacks the image in a column layout instead of row */
	stack?: boolean;
	/** image on right instead of left */
	reverse?: boolean;
	/** h3 instead of h2 title, smaller bottom margin */
	smaller?: boolean;
	/** small = 50%/50% columns; medium = 43.232%/56.768%; large = 20%/80% - default medium*/
	trailingBias?: 'small' | 'medium' | 'large';
	collapse?:
		| 'hide'
		| 'stack'
		| 'stackReverse'
		| 'large'
		| 'stackReverseLarge'
		| 'xLarge'
		| 'stackReverseXLarge';
	/** xs: 6 x grid, sm: 8 x grid, md: 10 x grid, lg: 12 x grid */
	paddingBottom?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
	/** padding on the side of the blurb adjacent to the image */
	blurbGutter?: 'medium';
	imageAlign?: 'center' | 'top' | 'bottom';
	/** default = medium; padding between the image and the blurb */
	imageGutter?: 'large' | 'medium' | 'small' | 'smaller' | 'none';
	/** default = center */
	imageJustify?: 'left' | 'right';
	/** md: 56px */
	responsivePaddingTop?: 'md';
	/** if true padding will be added below the image when the widget collapses */
	stackedPadding?: boolean;
	/** Apply the `fade` class so that the item is transitioned into view */
	animate?: boolean;
}

export interface SummaryChildren {
	image?: RenderResult;
	blurb: RenderResult;
	title?: RenderResult | (() => RenderResult);
}

const factory = create().properties<SummaryProperties>().children<SummaryChildren>();

export const Summary = factory(function Summary({ children, properties }) {
	const [{ title, image, blurb }] = children();
	const {
		animate,
		reverse,
		smaller,
		trailingBias = 'medium',
		collapse = 'stack',
		blurbGutter,
		imageAlign = 'center',
		imageGutter = 'medium',
		imageJustify,
		paddingBottom,
		responsivePaddingTop,
		stackedPadding = true,
		stack = false
	} = properties();

	const titleNode =
		typeof title === 'function' ? (
			title()
		) : !!title ? (
			<Text type={smaller ? 'subhead' : 'section'}>{title}</Text>
		) : undefined;

	let imageGutterClass: string | undefined;
	if (!stack) {
		if (imageGutter === 'medium') {
			if (trailingBias) {
				imageGutterClass = reverse ? css.leftPaddingMedium : css.rightPaddingMedium;
			} else {
				imageGutterClass = css.imagePaddingMedium;
			}
		} else if (imageGutter === 'small') {
			if (trailingBias) {
				imageGutterClass = reverse ? css.leftPaddingSmall : css.rightPaddingSmall;
			} else {
				imageGutterClass = css.imagePaddingSmall;
			}
		} else if (imageGutter === 'smaller') {
			if (trailingBias) {
				imageGutterClass = reverse ? css.leftPaddingSmaller : css.rightPaddingSmaller;
			} else {
				imageGutterClass = css.imagePaddingSmaller;
			}
		} else if (imageGutter === 'large') {
			imageGutterClass = reverse ? css.leftPaddingLarge : css.rightPaddingLarge;
		}
	}

	return (
		<div
			classes={[
				css.root,
				paddingBottom === 'xxs' && css.paddingBottomXXSmall,
				paddingBottom === 'xs' && css.paddingBottomXSmall,
				paddingBottom === 'sm' && css.paddingBottomSmall,
				paddingBottom === 'md' && css.paddingBottomMedium,
				paddingBottom === 'lg' && css.paddingBottomLarge,
				reverse && css.reverse,
				responsivePaddingTop === 'md' && css.responsivePaddingTopMedium,
				trailingBias === 'medium' && css.trailingBias,
				trailingBias === 'large' && css.largeTrailingBias,
				collapse === 'hide' && css.collapseHide,
				(collapse === 'stack' || collapse === 'stackReverse') && css.collapseStack,
				collapse === 'large' && css.collapseStackLarge,
				collapse === 'xLarge' && css.collapseStackXLarge,
				collapse === 'stackReverse' && css.collapseStackReverse,
				collapse === 'stackReverseLarge' && css.collapseStackReverseLarge,
				collapse === 'stackReverseXLarge' && css.collapseStackReverseXLarge,
				smaller && css.smaller,
				stack && css.stack,
				animate && 'fade'
			]}
		>
			{image && (
				<div
					classes={[
						css.leading,
						imageAlign === 'top' && css.imageTop,
						imageAlign === 'bottom' && css.imageBottom,
						imageGutterClass,
						imageJustify === 'left' && css.leftJustify,
						imageJustify === 'right' && css.rightJustify,
						(stack || stackedPadding) && css.stackedImagePadding
					]}
				>
					{image}
				</div>
			)}
			<div
				classes={[
					css.trailing,
					blurbGutter === 'medium' &&
						(reverse ? css.rightPaddingMedium : css.leftPaddingMedium)
				]}
			>
				{titleNode}
				<Text type="body">{blurb}</Text>
			</div>
		</div>
	);
});

export default Summary;
